import { motion } from 'framer-motion';
import { UserCheck } from '../icons';
import type { CoachTierBreakdown } from '../../lib/admin-dashboard';

interface CoachTierChartProps {
  tiers: CoachTierBreakdown[];
  totalCoaches: number;
}

const tierLabels: Record<string, string> = {
  basic: 'Basic',
  basic_plus: 'Basic Plus',
  advanced: 'Advanced',
  partner: 'Partner',
};

const tierColors: Record<string, string> = {
  basic: 'bg-gray-40',
  basic_plus: 'bg-brand-40',
  advanced: 'bg-success-40',
  partner: 'bg-orange-40',
};

export function CoachTierChart({ tiers, totalCoaches }: CoachTierChartProps) {
  if (tiers.length === 0) {
    return (
      <div className="freud-card-elevated p-6">
        <h3 className="text-text-xl text-gray-80 mb-4">Coach Tiers</h3>
        <p className="text-text-sm text-gray-40 text-center py-6">
          No coaches registered yet
        </p>
      </div>
    );
  }

  return (
    <div className="freud-card-elevated p-6">
      <div className="flex items-center gap-2 mb-5">
        <UserCheck className="w-5 h-5 text-success-40" />
        <h3 className="text-text-xl text-gray-80">Coach Tiers</h3>
      </div>
      <div className="space-y-4">
        {tiers.map((tier, index) => {
          const pct = totalCoaches > 0 ? Math.round((tier.count / totalCoaches) * 100) : 0;
          return (
            <motion.div
              key={tier.tier}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.08 }}
            >
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-text-sm text-gray-80 font-medium">
                    {tierLabels[tier.tier] || tier.tier}
                  </span>
                  {tier.withAiAccess > 0 && (
                    <span className="text-text-xs text-teal-50 bg-teal-10 px-2 py-0.5 rounded-full">
                      {tier.withAiAccess} AI
                    </span>
                  )}
                </div>
                <span className="text-text-sm text-gray-50 font-semibold">
                  {tier.count} ({pct}%)
                </span>
              </div>
              <div className="w-full bg-brand-10 rounded-full h-2">
                <motion.div
                  className={`${tierColors[tier.tier] || 'bg-gray-40'} h-2 rounded-full`}
                  initial={{ width: 0 }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                />
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
